<template>
  <div class="order">
    <div v-if="order">
      <v-layout column align-center justify-center>
        <v-col>
          <v-card
            elevation="4"
            class="mx-auto my-12 mb-5 px-10"
            min-width="320"
            max-width="600"
          >
            <v-card-title>{{ order.id }}</v-card-title>
            <v-card-subtitle>
              <span :class="getStatusTextClass()"
                >{{ order.status_message }} ({{
                  new Date(order.order.date_modified).toLocaleString()
                }})</span
              >
              <span v-if="showNoChargeMessage" class="text--secondary"
                ><br />
                Note - There is no usage charge for a
                {{ order.status.toLowerCase() }} order.
              </span>
            </v-card-subtitle>
            <v-card-text>
              <hr />
              <br />
              <v-row>
                <v-col>
                  Info

                  <div v-bind:style="{ fontSize: '14px' }" class="mx-4">
                    Account ID: {{ order.order.account }} <br />
                    User ID: {{ order.order.user_id }} <br />
                    <span v-if="order.order.select_id">
                      Select ID: {{ order.order.select_id }} <br />
                    </span>
                    Status:
                    <span :class="getStatusTextClass()">{{ order.status }}</span
                    ><br />
                    Duration: {{ order.order.duration }} <br />
                  </div>
                  <br />
                </v-col>
              </v-row>

              <div v-if="order.order.usage.cost.estimate">Usage Estimate</div>
              <div v-if="!order.order.usage.cost.estimate">Usage</div>

              <usage-table :usage="order.order.usage" :order_usage="true">
              </usage-table>

              <v-row v-if="showOrderStatusProgressBar">
                <v-col>
                  Status
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        <spinner
                          v-if="order.status.toLowerCase() === 'running'"
                          color="white"
                          ><span class="text--secondary">{{
                            `${this.order.status}... ${this.order.status_message}`
                          }}</span></spinner
                        >
                        <div class="d-flex align-center" v-else>
                          <v-icon
                            :class="{
                              'success--text':
                                order.status.toLowerCase() === 'succeeded',
                              'error--text':
                                order.status.toLowerCase() === 'failed',
                              'warning--text':
                                order.status.toLowerCase() === 'canceled',
                              'mr-2': true,
                            }"
                            style="vertical-align: middle"
                            >{{
                              order.status.toLowerCase() === "succeeded"
                                ? "mdi-check-bold"
                                : order.status.toLowerCase() === "canceled"
                                ? "mdi-cancel"
                                : order.status.toLowerCase() === "failed"
                                ? "mdi-alert"
                                : "mdi-info"
                            }}</v-icon
                          ><span :class="getStatusTextClass()">{{
                            order.status
                          }}</span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-stepper alt-labels>
                          <v-stepper-header>
                            <v-stepper-step
                              step="1"
                              :complete="
                                orderingHasError ? false : orderingFinished
                              "
                              :rules="
                                orderingHasError ? [() => false] : [() => true]
                              "
                              :color="
                                orderingHasError
                                  ? 'error'
                                  : orderingFinished
                                  ? 'success'
                                  : 'rgba(255, 255, 255, 0.5)'
                              "
                            >
                              <small class="text-center">
                                {{
                                  orderingFinished
                                    ? orderingHasError
                                      ? `Failed To Order ${orderFailedProgress} Acquisitions`
                                      : `Ordered ${orderProgress} Acquisitions`
                                    : orderingHasError
                                    ? `Failed To Order ${orderFailedProgress} Acquisitions`
                                    : `Ordered ${orderProgress} Acquisitions`
                                }}
                              </small>
                            </v-stepper-step>

                            <v-divider></v-divider>

                            <v-stepper-step
                              step="2"
                              :complete="
                                generatingHasError
                                  ? false
                                  : orderingFinished
                                  ? generatingFinished
                                  : null
                              "
                              :rules="
                                generatingHasError
                                  ? [() => false]
                                  : [() => true]
                              "
                              :color="
                                generatingHasError
                                  ? 'error'
                                  : generatingFinished
                                  ? 'success'
                                  : 'rgba(255, 255, 255, 0.5)'
                              "
                            >
                              <small class="text-center">
                                {{
                                  generatingFinished
                                    ? generatingHasError
                                      ? `Failed To Generate ${generatedFailedProgress} Acquisitions`
                                      : `Generated ${generatedProgress} Acquisitions`
                                    : generatingHasError
                                    ? `Failed To Generate ${generatedFailedProgress} Acquisitions`
                                    : `Generated ${generatedProgress} Acquisitions`
                                }}
                              </small>
                            </v-stepper-step>

                            <v-divider></v-divider>

                            <v-stepper-step
                              step="3"
                              :complete="
                                completingHasError
                                  ? false
                                  : orderingFinished && generatingFinished
                                  ? completingFinished
                                  : null
                              "
                              :rules="
                                completingHasError
                                  ? [() => false]
                                  : [() => true]
                              "
                              :color="
                                completingHasError
                                  ? 'error'
                                  : completingFinished
                                  ? 'success'
                                  : 'rgba(255, 255, 255, 0.5)'
                              "
                            >
                              <small class="text-center">
                                {{
                                  completingFinished
                                    ? completingHasError
                                      ? `Failed To Deliver ${completedFailedProgress} Acquisitions`
                                      : `Delivered ${completedProgress} Acquisitions`
                                    : completingHasError
                                    ? `Failed To Deliver ${completedFailedProgress} Acquisitions`
                                    : `Delivered ${completedProgress} Acquisitions`
                                }}
                              </small>
                            </v-stepper-step>
                          </v-stepper-header>
                        </v-stepper>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <br />
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <div>Details</div>
                  <br />
                  <v-row>
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Settings
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div
                              v-bind:style="{ fontSize: '14px' }"
                              class="mx-4"
                            >
                              <span v-if="order.order.settings">
                                Bundle Adjust:
                                {{ order.order.settings.bundle_adjust }}
                                <br />
                                Water Mask:
                                {{ order.order.settings.water_mask }}
                                <br />
                                <span v-if="is_internal">
                                  P3DR: {{ order.order.settings.p3dr }}
                                  <br />
                                </span>
                                HD:
                                {{ order.order.settings.hd }}
                                <br />
                                Cloud Mask:
                                {{ order.order.settings.cloud_mask }}
                                <br />
                                Multispectral Saturation Mask:
                                {{ order.order.settings.ms_saturation_mask }}
                                <br />
                                Healthy Vegetation Mask:
                                {{
                                  order.order.settings.healthy_vegetation_mask
                                }}
                                <br />
                                Terrain Shadow Mask:
                                {{ order.order.settings.terrain_shadow_mask }}
                                <br />
                                Panchromatic Flare Mask:
                                {{ order.order.settings.pan_flare_mask }}
                                <br />
                                <span v-if="is_internal">
                                  Data Mask:
                                  {{ order.order.settings.data_mask }}
                                  <br />
                                </span>
                                <span v-if="is_internal">
                                  Visual Tile: {{ order.order.settings.visual }}
                                  <br />
                                </span>
                                <span v-if="is_internal">
                                  Multispectral Tile:
                                  {{ order.order.settings.ms_analytic }} <br />
                                </span>
                                <span v-if="is_internal">
                                  Panchromatic Tile:
                                  {{ order.order.settings.pan_analytic }} <br />
                                </span>
                              </span>
                              <span v-else-if="order.order.bundle_adjust">
                                Bundle Adjust:
                                {{ order.order.bundle_adjust }}
                                <br />
                              </span>
                              <span v-else>
                                No settings to display
                                <br />
                              </span>
                              <span
                                v-if="
                                  is_internal &&
                                  order.order.settings !== null &&
                                  order.order.settings.building_mask
                                "
                              >
                                Building Mask:
                                {{ order.order.settings.building_mask }}
                                <br />
                              </span>
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Acquisition Details
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre>{{ order.acquisition_details }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row v-show="order.order.metadata">
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Metadata
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre>{{ order.order.metadata }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Output Config
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre>{{ order.order.output_config }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row v-show="order.order.intersects">
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Intersects
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <!-- <pre>{{ order.order.intersects }}</pre> -->
                            <div style="min-height: 256px; height: 100%">
                              <Map
                                :zoom-control="true"
                                :min-zoom="3"
                                :max-zoom="19"
                                :copyTools="getPolygon"
                                :polygons="getPolygon"
                                :bounds="getBounds"
                                :bounds-options="{ padding: 50 }"
                                map-style="mapbox://styles/mapbox/dark-v10"
                              />
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row v-show="order.order.bbox">
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Bounding Box
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <!-- <pre>{{ order.order.bbox }}</pre> -->
                            <div style="min-height: 256px; height: 100%">
                              <Map
                                :zoom-control="true"
                                :min-zoom="3"
                                :max-zoom="19"
                                :copyTools="bboxPolygon"
                                :polygons="bboxPolygon"
                                :bounds="bboxBounds"
                                :bounds-options="{ padding: 50 }"
                                map-style="mapbox://styles/mapbox/dark-v10"
                              />
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>

                  <v-row v-show="order.order.notifications">
                    <v-col>
                      <v-expansion-panels>
                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            Notifications
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <pre>{{ order.order.notifications }}</pre>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <div class="float-right caption">
            <div>
              Order created:
              {{ new Date(order.order.date_created).toLocaleString() }}
              &nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp; Last modified:
              {{ new Date(order.order.date_modified).toLocaleString() }}
            </div>
          </div>
        </v-col>
      </v-layout>
    </div>
  </div>
</template>

<script>
import UsageTable from "@/components/usage/UsageTable.vue";
import Map from "@/components/map/index";
import Spinner from "@/components/shared/Spinner";
import turfBbox from "@turf/bbox";
import turfBboxPoly from "@turf/bbox-polygon";
import Wkt from "wicket";
import { mapGetters } from "vuex";

export default {
  props: ["order"],
  data() {
    return {
      usage_range_start: null,
      usage_range_end: null,
      error: [() => true],
    };
  },
  components: {
    Map,
    UsageTable,
    Spinner,
  },
  computed: {
    ...mapGetters(["account_details", "showOrderStatusProgressBar"]),

    showNoChargeMessage() {
      const lowerStatus = this.order.status.toLowerCase();
      return lowerStatus === "failed" || lowerStatus === "canceled";
    },
    is_internal() {
      if (this.account_details) {
        return this.account_details.internal;
      }
      return false;
    },
    getPolygon() {
      return [
        {
          type: "Feature",
          geometry: this.parseGeom(this.order.order.intersects),
        },
      ];
    },
    getBounds() {
      return turfBbox({
        type: "Feature",
        geometry: this.parseGeom(this.order.order.intersects),
      });
    },
    bboxPolygon() {
      if (this.order.order.bbox) {
        return [turfBboxPoly(this.order.order.bbox)];
      } else {
        return null;
      }
    },
    bboxBounds() {
      if (this.order.order.bbox) {
        return turfBbox(turfBboxPoly(this.order.order.bbox));
      } else {
        return null;
      }
    },

    isRunning() {
      return this.order.status === "RUNNING";
    },

    orderProgress() {
      const details = this.getOrderingDetails();
      return `${details.orderedCount}/${details.acquisitionLength}`;
    },

    orderFailedProgress() {
      const details = this.getOrderingDetails();
      return `${details.orderFailedCount}/${details.acquisitionLength}`;
    },

    orderingFinished() {
      const details = this.getOrderingDetails();
      return (
        details.orderedCount === details.acquisitionLength || !this.isRunning
      );
    },

    orderingHasError() {
      const details = this.getOrderingDetails();
      return (
        details.orderFailedCount >
        (!this.isRunning && details.orderedCount !== details.acquisitionLength)
      );
    },

    generatedProgress() {
      const details = this.getGeneratingDetails();
      return `${details.generatedCount}/${details.acquisitionLength}`;
    },

    generatedFailedProgress() {
      const details = this.getGeneratingDetails();
      return `${details.generatedFailedCount}/${details.acquisitionLength}`;
    },

    generatingFinished() {
      const details = this.getGeneratingDetails();
      return (
        details.generatedCount === details.acquisitionLength || !this.isRunning
      );
    },

    // NO_INTERSECTING_TILES is only a failure if the overall order failse
    generatingHasError() {
      const details = this.getGeneratingDetails();
      return (
        details.generatedFailedCount > 0 ||
        (!this.isRunning &&
          details.generatedCount !== details.acquisitionLength)
      );
    },

    completedProgress() {
      const details = this.getCompletedDetails();
      return `${details.completedCount}/${details.acquisitionLength}`;
    },
    completedFailedProgress() {
      const details = this.getCompletedDetails();
      return `${details.completedFailedCount}/${details.acquisitionLength}`;
    },
    completingFinished() {
      const details = this.getCompletedDetails();
      return (
        details.completedCount === details.acquisitionLength || !this.isRunning
      );
    },
    completingHasError() {
      const details = this.getCompletedDetails();
      return (
        !this.isRunning && details.completedCount !== details.acquisitionLength
      );
    },
  },
  methods: {
    parseGeom(geom) {
      if (typeof geom === "string") {
        var wkt = new Wkt.Wkt();
        wkt.read(this.order.order.intersects);
        geom = wkt.toJson();
      }
      return geom;
    },
    getAcquisitionDetails() {
      const orderingCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "Ordering 1B"
      ).length;

      const orderFailedCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "1B order failed"
      ).length;

      const generatingCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "Generating ARD tiles"
      ).length;

      const generationFailedCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "ARD tile generation failed"
      ).length;

      const deliveringCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "Delivering ARD tiles to customer"
      ).length;

      const deliveryFailedCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "ARD tile delivery failed"
      ).length;

      const succeededCount = this.order.acquisition_details.filter(
        (object) => object.status_message === "ARD for acquisition is complete"
      ).length;

      const noIntersectingCount = this.order.acquisition_details.filter(
        (object) =>
          object.status_message ===
          "Requested cell_ids do not intersect the acquisition and/or aoi"
      ).length;

      const acquisitionFailedCount = this.order.acquisition_details.filter(
        (object) =>
          object.status_message ===
          "Acquisition aborted due to another acquisition failure"
      ).length;

      const acquisitionCanceledCount = this.order.acquisition_details.filter(
        (object) =>
          object.status_message ===
          "Acquisition did not finish processing because order was canceled."
      ).length;

      return {
        orderingCount,
        orderFailedCount,
        generatingCount,
        generationFailedCount,
        deliveringCount,
        deliveryFailedCount,
        succeededCount,
        noIntersectingCount,
        acquisitionFailedCount,
        acquisitionCanceledCount,
      };
    },
    getOrderingDetails() {
      const details = {};
      const acqDetails = this.getAcquisitionDetails();

      details.orderedCount =
        acqDetails.generatingCount +
        acqDetails.generationFailedCount +
        acqDetails.deliveringCount +
        acqDetails.succeededCount +
        acqDetails.noIntersectingCount +
        acqDetails.deliveryFailedCount +
        acqDetails.acquisitionFailedCount +
        acqDetails.acquisitionCanceledCount;

      details.orderFailedCount = acqDetails.orderFailedCount;
      details.acquisitionLength = this.order.acquisition_details.length;

      return details;
    },
    getGeneratingDetails() {
      const details = {};
      const acqDetails = this.getAcquisitionDetails();

      details.generatedCount =
        acqDetails.deliveringCount +
        acqDetails.succeededCount +
        acqDetails.noIntersectingCount +
        acqDetails.deliveryFailedCount;

      details.generatedFailedCount = acqDetails.generationFailedCount;
      details.acquisitionLength = this.order.acquisition_details.length;

      return details;
    },
    getCompletedDetails() {
      const details = {};
      const acqDetails = this.getAcquisitionDetails();

      details.completedCount = acqDetails.succeededCount;
      details.completedFailedCount =
        acqDetails.deliveryFailedCount + acqDetails.acquisitionFailedCount;
      details.acquisitionLength = this.order.acquisition_details.length;

      return details;
    },
    getStatusTextClass() {
      let cls = "";
      switch (this.order.status.toLowerCase()) {
        case "failed":
          cls = "error--text";
          break;
        case "canceled":
          cls = "warning--text";
          break;
        case "succeeded":
          cls = "success--text";
          break;
        case "running":
          cls = "text--secondary";
          break;
      }

      return cls;
    },
  },
};
</script>

<style lang="scss" scoped>
pre {
  white-space: pre-wrap;
}
</style>
